import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {FiltersBar} from '@macmillanplatform/ui-core';
import {
  useAbortedEffect,
  useStateFromUrl,
} from '@macmillanplatform/ui-core/hooks';

import {CONFIGS_FIELDS} from '@/constants';

import {generateGridProps, getFiltersFields, getStateFromUrl} from './helpers';
import {useAppDispatch, useInitUrlParams} from '@/hooks';
import {browserHistory} from '@/shared/utils';

import {
  clearCustomersShipNames,
  fetchCustomersBillNames,
  fetchCustomersShipNames,
} from '../../actions';
import {selectCustomersBillName, selectCustomersShipName} from '../../reducer';
import {fetchOrders} from '@/actions';
import {
  selectConfigField,
  selectDefaultFields,
  selectOrders,
  selectOrdersCount,
  selectOrdersLoading,
} from '@/reducer';

import {getOrdersFormatters} from './components/orders-formatters';
import {DataTable} from '@/shared/components';

import './index.css';

const Orders = () => {
  const dispatch = useAppDispatch();

  const orders = useSelector(selectOrders);
  const ordersCount = useSelector(selectOrdersCount);
  const ordersIsLoading = useSelector(selectOrdersLoading);
  const fields = useSelector(selectConfigField(CONFIGS_FIELDS.ORDER_FIELDS));
  const defaultFields = useSelector(
    selectDefaultFields(CONFIGS_FIELDS.FIELDS_ORDERS)
  );
  const customersBillName = useSelector(selectCustomersBillName);
  const customersShipName = useSelector(selectCustomersShipName);

  const [stateFromUrl] = useStateFromUrl(getStateFromUrl, browserHistory);
  const {searchPageValues, filtersValues} = stateFromUrl;
  const {ordering, limit, offset} = searchPageValues;
  const {q} = filtersValues;

  const filtersFields = useMemo(() => getFiltersFields({q}), [q]);

  const greetings = !!customersBillName
    ? `Welcome, ${customersBillName}!`
    : 'Welcome!';

  useInitUrlParams({dataFromUrl: {...filtersValues, ...searchPageValues}});

  useAbortedEffect(
    ({signal}) => {
      const urlParams = browserHistory.getParametersObject();

      dispatch(fetchOrders({urlParams, signal}));
      dispatch(fetchCustomersBillNames());
    },
    [ordering, limit, offset, q]
  );

  useEffect(() => {
    q?.trim()
      ? dispatch(fetchCustomersShipNames({shipTo: q}))
      : dispatch(clearCustomersShipNames());
  }, [q, dispatch]);

  const gridProps = useMemo(generateGridProps, []);
  const formatters = useMemo(getOrdersFormatters, []);
  return (
    <div className="Orders">
      <div className="Orders__header">
        <h2 className="Orders__header-title">{greetings}</h2>

        <div className="Orders__data-table-header">
          <FiltersBar
            className="Orders__filters"
            filtersFields={filtersFields}
            filtersValues={filtersValues}
          />
          <span className="Orders__data-table-description">
            <i className="fa-solid fa-circle-info" /> Six months of orders and
            all backorders are available
          </span>
        </div>
      </div>

      <div className="Orders__table-wrapper">
        {!!q.length ? (
          <span className="Orders__table-search">
            <i className="fa-solid fa-magnifying-glass" /> Showing {ordersCount}{' '}
            results for: {customersShipName || q}
          </span>
        ) : (
          <span className="Orders__table-title">Purchase Orders</span>
        )}
        <DataTable
          {...gridProps}
          count={ordersCount}
          defaultFields={defaultFields}
          fields={fields}
          formatters={formatters}
          gridData={orders}
          limit={limit}
          loading={ordersIsLoading}
          offset={offset}
          ordering={ordering}
          isSelectable={false}
        />
      </div>
    </div>
  );
};

export default Orders;
